import { Box, Chip, CircularProgress, Stack } from "@mui/joy";
import { ApiLine, ApiVenueMap, ClimbType } from "griptonite-api-client";
import groupBy from "lodash/groupBy";
import sortBy from "lodash/sortBy";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useShallow } from "zustand/react/shallow";

import { useRoutesUiStore } from "@/components/RoutesProvider";

import { Route } from "../../core/types";
import { useVenueZonesByClimbType } from "../../hooks/routes";
import { useIsKeyDownRef } from "../../hooks/useIsKeyDown";
import { useCurrentVenueUuid } from "../../hooks/venues";
import { useRoutesTableStore } from "../RoutesTable/RoutesProvider2";
import { CurrentlyDraggingRoutePin } from "./CurrentlyDraggingRoutePin";
import Map from "./Map";
import MapConfigDropdown from "./MapConfigDropdown";
import { MapLineIndicator } from "./MapLinePinSvg";
import MapPinsForLineSvg from "./MapPinsForLineSvg";
import MapRoutePinSvg from "./MapRoutePinSvg";
import UnplacedRoutesDrawer from "./UnplacedRoutesDrawer";

type BoulderProps = {
  routes?: ({ uuid: string } & Partial<Route>)[];
  maps: ApiVenueMap[];
  selectedRouteUuids: Set<string>;
  filteredRouteUuids: Set<string>;
  canChangeSelection: boolean;
  onSelectRoutes: (uuids: string[]) => void;
  onDeselectRoutes: (uuids: string[]) => void;
  onSelectSingleRoute: (uuid: string) => void;
  onClearSelection: () => void;
  onSetSelectedRouteUuids: (uuids: string[]) => void;
  onRouteContextMenu: (uuid: string, x: number, y: number) => void;
  onMapContextMenu: (
    mapUuid: string,
    mapPosition: { x: number; y: number },
    x: number,
    y: number,
  ) => void;
  onBrushStart: () => void;
  brushEnabled: boolean;
  routesPending?: boolean;
  mapsPending?: boolean;
};

type SportProps = {
  lines?: ApiLine[];
  routes?: ({ uuid: string } & Partial<Route>)[];
  maps: ApiVenueMap[];
  canChangeSelection: boolean;
  selectedRouteUuids: Set<string>;
  filteredRouteUuids: Set<string>;
  onSelectRoutes: (uuids: string[]) => void;
  onDeselectRoutes: (uuids: string[]) => void;
  onSelectSingleRoute: (uuid: string) => void;
  onClearSelection: () => void;
  onSetSelectedRouteUuids: (uuids: string[]) => void;
  onSelectLine: (lineUuid: string | null, routeUuids: string[]) => void;
  onRouteContextMenu: (uuid: string, x: number, y: number) => void;
  onLineContextMenu: (uuid: string, x: number, y: number) => void;
  onMapContextMenu?: (
    mapUuid: string,
    mapPosition: { x: number; y: number },
    x: number,
    y: number,
  ) => void;
  brushEnabled: boolean;
  routesPending?: boolean;
  mapsPending?: boolean;
};

export type RouteWithMap = Omit<Route, "mapPosition" | "map"> & {
  map: string;
  mapPosition: { x: number; y: number };
};

const emptyObject = {} as Record<string, never>;
const emptyArray = [] as never[];

export const MOUSE_OFFSET = { x: 0, y: 0 };

// Pins keep a fixed screen size until the map's scale (screen px per map unit) reaches the
// cap, then grow with the map.
const ROUTE_PIN_SCALE_CAP = 4;
const LINE_SCALE_CAP = 1.5;

export const pointIsInElementBoundingClientRect = (x: number, y: number, el: HTMLElement) => {
  const rect = el.getBoundingClientRect();
  return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
};

const MapInfoDisplay = ({ map, climbType }: { map: ApiVenueMap; climbType: ClimbType }) => {
  const currentVenueUuid = useCurrentVenueUuid();
  const { data: zones } = useVenueZonesByClimbType(currentVenueUuid, climbType);
  const { setFilterParam, filterParams } = useRoutesTableStore(
    useShallow((state) => ({
      setFilterParam: state.setFilterParam,
      filterParams: state.filterParams,
    })),
  );

  const filterZones = filterParams.zone?.matches;

  return (
    <Stack
      sx={{
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 0,
        color: "#FFFFFF99",
        padding: 1,
        fontSize: 14,
        zIndex: 10,
        overflow: "auto",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        "&::-webkit-scrollbar": {
          display: "none",
        },
        alignItems: "center",
        pointerEvents: "none",
      }}
      spacing={0.5}
      direction="row"
    >
      <Box sx={{ paddingRight: 0.5, flexShrink: 0 }}>{map.name}</Box>
      {zones?.map((zone) => (
        <Chip
          variant={filterZones?.has(zone.uuid) ? "plain" : "solid"}
          key={zone.uuid}
          onClick={() => setFilterParam("zone", { matches: new Set([zone.uuid]) })}
          slotProps={{
            action: {
              sx: {
                pointerEvents: "all",
                backgroundColor: "var(--joy-palette-neutral-800)",
                "&:hover": {
                  backgroundColor: "var(--joy-palette-neutral-700)",
                },
              },
            },
            label: {
              sx: {
                color: "var(--joy-palette-neutral-100)",
                fontSize: 12,
              },
            },
          }}
        >
          {zone.name}
        </Chip>
      ))}
    </Stack>
  );
};

export const MapProgress = () => (
  <CircularProgress
    sx={{
      "--CircularProgress-progressColor": "var(--joy-palette-neutral-400)",
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      zIndex: 100,
    }}
    variant="plain"
    thickness={2}
  />
);

export const BoulderMaps = ({
  maps,
  routes,
  selectedRouteUuids,
  canChangeSelection,
  onSelectRoutes,
  onDeselectRoutes,
  onSelectSingleRoute,
  onClearSelection,
  onSetSelectedRouteUuids,
  onRouteContextMenu,
  onMapContextMenu,
  filteredRouteUuids,
  brushEnabled,
  routesPending,
  mapsPending,
}: BoulderProps) => {
  const mapPositionEdits = useRoutesUiStore((s) => s.mapPositionEdits);

  const routesByMap = useMemo(() => {
    if (!routes) {
      return emptyObject as Record<string, RouteWithMap[]>;
    }
    const result: Record<string, RouteWithMap[]> = {};
    sortBy(routes, (x) => (filteredRouteUuids.has(x.uuid) ? 1 : 0)).forEach((route) => {
      const editedPos = mapPositionEdits[route.uuid];
      let mapKey = route.map && route.mapPosition ? route.map : "";
      let mapPosition = route.mapPosition;
      if (editedPos && editedPos.map && editedPos.x !== null && editedPos.y !== null) {
        mapKey = editedPos.map;
        mapPosition = { x: editedPos.x, y: editedPos.y };
      }
      result[mapKey] = result[mapKey] || [];
      result[mapKey].push({ ...route, mapPosition } as RouteWithMap);
    });
    return result;
  }, [routes, mapPositionEdits, filteredRouteUuids]);

  // TODO: find a better way to do this globally!
  const isShiftPressedRef = useIsKeyDownRef("Shift");

  const onSetSelectedIds = useCallback(
    (ids: string[]) => {
      ids = ids.filter((id) => filteredRouteUuids.has(id));
      if (!canChangeSelection) {
        return;
      }
      console.log("isShiftPressed", isShiftPressedRef.current);
      if (isShiftPressedRef.current) {
        onSelectRoutes(ids);
      } else {
        onSetSelectedRouteUuids(ids);
      }
    },
    [
      canChangeSelection,
      onSelectRoutes,
      onSetSelectedRouteUuids,
      isShiftPressedRef,
      filteredRouteUuids,
    ],
  );

  const onClick = useCallback(
    (uuid: string) => {
      if (!canChangeSelection || !filteredRouteUuids.has(uuid)) {
        return;
      }
      const isSelected = selectedRouteUuids.has(uuid);
      console.log({
        isSelected,
        isShiftPressed: isShiftPressedRef.current,
        uuid,
      });
      if (isShiftPressedRef.current) {
        if (isSelected) {
          onDeselectRoutes([uuid]);
        } else {
          onSelectRoutes([uuid]);
        }
      } else {
        if (isSelected) {
          onClearSelection();
        } else {
          onSelectSingleRoute(uuid);
        }
      }
    },
    [
      canChangeSelection,
      selectedRouteUuids,
      onSelectSingleRoute,
      onSelectRoutes,
      onDeselectRoutes,
      onClearSelection,
      isShiftPressedRef,
      filteredRouteUuids,
    ],
  );

  return (
    <Stack
      direction="row"
      sx={{
        minHeight: 200,
        backgroundColor: "neutral.900",
        height: "100%",
        position: "relative",
      }}
    >
      <MapConfigDropdown />
      {mapsPending ? <MapProgress /> : null}
      {maps.map((map) => (
        <BoulderMap
          key={map.uuid}
          map={map}
          routes={routesByMap[map.uuid] || emptyArray}
          onClick={onClick}
          selectedRouteUuids={selectedRouteUuids}
          filteredRouteUuids={filteredRouteUuids}
          onRouteContextMenu={onRouteContextMenu}
          brushEnabled={brushEnabled}
          onClearSelection={onClearSelection}
          onSetSelectedUuids={onSetSelectedIds}
          onMapContextMenu={onMapContextMenu}
          canChangeSelection={canChangeSelection}
          routesPending={routesPending}
        />
      ))}
      <CurrentlyDraggingRoutePin />
    </Stack>
  );
};

export type LineRoutes = {
  line: ApiLine;
  routes: Partial<Route>[];
  mapPosition: { x: number; y: number };
};

export const BoulderMap = ({
  map,
  routes,
  onClick,
  selectedRouteUuids,
  filteredRouteUuids,
  onRouteContextMenu,
  brushEnabled,
  onClearSelection,
  onSetSelectedUuids,
  onMapContextMenu,
  canChangeSelection,
  routesPending,
}: {
  map: ApiVenueMap;
  routes: RouteWithMap[];
  onClick: (uuid: string) => void;
  selectedRouteUuids: Set<string>;
  filteredRouteUuids: Set<string>;
  onRouteContextMenu?: (uuid: string, x: number, y: number) => void;
  brushEnabled: boolean;
  onClearSelection: () => void;
  onSetSelectedUuids: (uuids: string[]) => void;
  onMapContextMenu: (
    mapUuid: string,
    mapPosition: { x: number; y: number },
    x: number,
    y: number,
  ) => void;
  canChangeSelection: boolean;
  routesPending?: boolean;
}) => {
  const [zoomState, setZoomState] = useState<
    | {
        center: { x: number; y: number };
        scale: number;
      }
    | undefined
  >(undefined);

  const onClickWrapped = (uuid: string) => {
    isLocalChangeRef.current = true;
    onClick(uuid);
  };
  const onSetSelectedUuidsWrapped = (uuids: string[]) => {
    isLocalChangeRef.current = true;
    onSetSelectedUuids(uuids);
  };

  const routesRef = useRef(routes);
  useEffect(() => {
    routesRef.current = routes;
  }, [routes]);

  const isLocalChangeRef = useRef(false);

  useEffect(() => {
    // debugger;
    if (!isLocalChangeRef.current) {
      if (selectedRouteUuids.size === 1) {
        const mapPos = routesRef.current.find((r) => selectedRouteUuids.has(r.uuid))?.mapPosition;
        if (!mapPos) {
          return;
        }
        setZoomState({
          center: mapPos,
          scale: 4,
        });
      } else {
        setZoomState(undefined);
      }
    }
    isLocalChangeRef.current = false;
  }, [selectedRouteUuids]);

  return (
    <Box
      sx={{
        flex: 1,
        position: "relative",
        borderLeft: "1px solid #FFFFFF44",
        "&:first-of-type": {
          borderLeft: "none",
        },
        paddingBottom: 2,
      }}
      onClick={() => {
        if (canChangeSelection) {
          onClearSelection();
        }
      }}
    >
      <MapInfoDisplay map={map} climbType="BOULDER" />
      <Map
        key={map.uuid}
        map={map}
        mapObjects={routes}
        zoomToBbox={false}
        bboxPadding={10}
        maxZoom={10}
        selectedIds={selectedRouteUuids}
        onSetSelectedIds={onSetSelectedUuidsWrapped}
        idAccessor={(x) => x.uuid}
        brushEnabled={brushEnabled && canChangeSelection}
        zoomState={zoomState}
        onContextMenu={(e, x, y) => {
          e.preventDefault();
          onMapContextMenu(map.uuid, { x, y }, e.clientX, e.clientY);
        }}
        renderMapObject={(route, transform, isSelected) => {
          const isFilteredOut = route.uuid !== "" && !filteredRouteUuids.has(route.uuid);

          return (
            <g
              transform={`translate(${route.mapPosition.x}, ${route.mapPosition.y})`}
              key={route.uuid}
            >
              <g
                transform={`scale(${
                  getScaleFactor(isSelected, isFilteredOut, selectedRouteUuids.size > 0) /
                  Math.min(transform.k, ROUTE_PIN_SCALE_CAP)
                })`}
                opacity={isSelected || selectedRouteUuids.size === 0 ? 1 : 0.5}
              >
                <MapRoutePinSvg
                  hideText={isFilteredOut}
                  route={route}
                  onClick={(e) => {
                    e.stopPropagation();
                    onClickWrapped(route.uuid);
                  }}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    onRouteContextMenu?.(route.uuid, e.clientX, e.clientY);
                  }}
                  dragToMoveEnabled={isSelected && selectedRouteUuids.size === 1}
                />
              </g>
            </g>
          );
        }}
      />
      {routesPending ? <MapProgress /> : null}
    </Box>
  );
};

export const SportMap = ({
  map,
  linesRoutes,
  nonLineRoutes,
  onClick,
  onSelectLine,
  onSelectLines,
  selectedLineUuids,
  expandedLineUuid,
  onExpandLine,
  selectedRouteUuids,
  filteredRouteUuids,
  onRouteContextMenu,
  onLineContextMenu,
  canChangeSelection,
  brushEnabled,
  routesPending,
  showMapInfo = true,
}: {
  map: ApiVenueMap;
  linesRoutes: LineRoutes[];
  nonLineRoutes: Partial<Route>[];
  selectedRouteUuids: Set<string>;
  filteredRouteUuids: Set<string>;
  onClick: (uuid: string) => void;
  onSelectLine?: (lineUuid: string | null, routeUuids: string[]) => void;
  onSelectLines?: (lineUuids: string[], routeUuids: string[]) => void;
  selectedLineUuids: Set<string>;
  expandedLineUuid: string | null;
  onExpandLine: (lineUuid: string | null) => void;
  onRouteContextMenu?: (uuid: string, x: number, y: number) => void;
  onLineContextMenu?: (uuid: string, x: number, y: number) => void;
  canChangeSelection: boolean;
  brushEnabled: boolean;
  routesPending?: boolean;
  showMapInfo?: boolean;
}) => {
  const onSelectLineRef = useRef(onSelectLine);
  useLayoutEffect(() => {
    onSelectLineRef.current = onSelectLine;
  }, [onSelectLine]);

  const [zoomState, setZoomState] = useState<
    | {
        center: { x: number; y: number };
        scale: number;
      }
    | undefined
  >(undefined);

  useEffect(() => {
    if (!expandedLineUuid) {
      onSelectLineRef.current?.(null, []);
      return;
    }
    const line = linesRoutes?.find((lineRoutes) => lineRoutes.line.uuid === expandedLineUuid);
    onSelectLineRef.current?.(
      line?.line.uuid || null,
      line?.routes.map((r) => r.uuid).filter((uuid): uuid is string => uuid !== undefined) || [],
    );
    if (!line || !line.line.mapPosition) {
      setZoomState(undefined);
    } else {
      setZoomState({
        center: line.line.mapPosition,
        scale: 4,
      });
    }
  }, [expandedLineUuid, linesRoutes]);

  const onSetSelectedIds = (lineUuids: string[]) => {
    onSelectLines?.(
      lineUuids,
      linesRoutes
        .filter((l) => lineUuids.includes(l.line.uuid))
        .flatMap((l) =>
          l.routes.map((r) => r.uuid).filter((uuid): uuid is string => uuid !== undefined),
        ),
    );
  };

  // Combined map objects for rendering - includes both line routes and non-line routes
  const allMapObjects = useMemo(() => {
    const lineObjects = linesRoutes || [];
    const nonLineObjects = (nonLineRoutes || [])
      .filter((route) => route.mapPosition && route.uuid)
      .map((route) => ({
        type: "nonLineRoute" as const,
        route,
        uuid: route.uuid!,
        mapPosition: route.mapPosition!,
      }));

    return [...lineObjects, ...nonLineObjects];
  }, [linesRoutes, nonLineRoutes]);

  const onSetSelectedIdsForAll = (ids: string[]) => {
    // Separate line UUIDs from route UUIDs
    const lineUuids = ids.filter((id) => linesRoutes.some((lr) => lr.line.uuid === id));
    const nonLineRouteUuids = ids.filter((id) => nonLineRoutes.some((r) => r.uuid === id));

    if (lineUuids.length > 0) {
      onSetSelectedIds(lineUuids);
    }

    // Handle non-line route selection if needed
    if (nonLineRouteUuids.length > 0 && canChangeSelection) {
      nonLineRouteUuids.forEach((uuid) => onClick(uuid));
    }
  };

  return (
    <Box
      sx={{
        // flex: (1000 * map.width) / map.height,
        flex: 1,
        borderLeft: "1px solid #FFFFFF44",
        "&:first-of-type": {
          borderLeft: "none",
        },
        position: "relative",
      }}
      // onClick={onClearSelection}
    >
      {showMapInfo ? <MapInfoDisplay map={map} climbType="SPORT" /> : null}
      <Map
        key={map.uuid}
        brushEnabled={brushEnabled && canChangeSelection}
        map={map}
        mapObjects={allMapObjects}
        zoomToBbox={false}
        zoomState={zoomState}
        bboxPadding={10}
        maxZoom={10}
        selectedIds={selectedLineUuids}
        onSetSelectedIds={onSetSelectedIdsForAll}
        idAccessor={(x) => ("line" in x ? x.line.uuid : x.uuid)}
        onZoomChange={() => onExpandLine(null)}
        renderMapObject={(mapObject, transform, isSelected) => {
          // Handle non-line routes
          if ("type" in mapObject && mapObject.type === "nonLineRoute") {
            const route = mapObject.route;
            // Don't render non-line routes when a line is expanded
            if (expandedLineUuid) {
              return null;
            }

            const isRouteSelected = selectedRouteUuids.has(route.uuid!);
            const isRouteFilteredOut = route.uuid && !filteredRouteUuids.has(route.uuid!);
            let opacity = 1;
            if (selectedLineUuids.size > 0 || selectedRouteUuids.size > 0) {
              opacity = isRouteSelected ? 1 : 0.5;
            }
            if (filteredRouteUuids.size > 0 && isRouteFilteredOut) {
              opacity = 0.5;
            }

            return (
              <g
                key={route.uuid}
                transform={`translate(${mapObject.mapPosition.x}, ${mapObject.mapPosition.y})`}
                opacity={opacity}
              >
                <g transform={`scale(${1 / Math.min(transform.k, ROUTE_PIN_SCALE_CAP)})`}>
                  <MapRoutePinSvg
                    route={route as Route}
                    onClick={(e) => {
                      e.stopPropagation();
                      onClick(route.uuid!);
                    }}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      onRouteContextMenu?.(route.uuid!, e.clientX, e.clientY);
                    }}
                    dragToMoveEnabled={isRouteSelected && selectedRouteUuids.size === 1}
                  />
                </g>
              </g>
            );
          }

          // Handle line routes (existing logic)
          const lineRoutes = mapObject as LineRoutes;
          // The line group stops counter-scaling at LINE_SCALE_CAP. Route pins inside it, and
          // the expanded ring's radius, scale like every other route pin.
          const lineScale = Math.min(transform.k, LINE_SCALE_CAP);
          const routePinScale = lineScale / Math.min(transform.k, ROUTE_PIN_SCALE_CAP);
          const pinScreenMagnification = Math.max(1, transform.k / ROUTE_PIN_SCALE_CAP);
          let opacity = 1;
          const isLineExpanded = expandedLineUuid === lineRoutes.line.uuid;
          if (expandedLineUuid && !isLineExpanded) {
            opacity = 0;
          } else if (expandedLineUuid && isLineExpanded) {
            opacity = 1;
          } else if (selectedLineUuids.size > 0 && !isSelected) {
            opacity = 0.5;
          }

          return (
            <g
              key={lineRoutes.line.uuid}
              transform={`translate(${lineRoutes.mapPosition.x}, ${lineRoutes.mapPosition.y})`}
              opacity={opacity}
              pointerEvents={expandedLineUuid === null || isLineExpanded ? "auto" : "none"}
            >
              <g transform={`scale(${1 / lineScale})`}>
                {/* An empty line has no pins to expand into, so it keeps its marker. */}
                {isLineExpanded && lineRoutes.routes.length > 0 ? (
                  <MapPinsForLineSvg
                    lineRoutes={lineRoutes}
                    routePinScale={routePinScale}
                    onRouteClick={(e: React.MouseEvent, routeUuid: string) => {
                      e.stopPropagation();
                      onClick(routeUuid);
                    }}
                    onContextMenu={
                      onRouteContextMenu &&
                      ((e: React.MouseEvent, routeUuid: string) => {
                        e.preventDefault();
                        onRouteContextMenu(routeUuid, e.clientX, e.clientY);
                      })
                    }
                    selectedRouteUuids={selectedRouteUuids}
                    filteredRouteUuids={filteredRouteUuids}
                  />
                ) : (
                  <MapLineIndicator
                    lineRoutes={lineRoutes}
                    routePinScale={routePinScale}
                    screenMagnification={pinScreenMagnification}
                    onClick={() => onExpandLine(lineRoutes.line.uuid)}
                    onContextMenu={
                      onLineContextMenu &&
                      ((e, lineUuid) => {
                        e.preventDefault();
                        onLineContextMenu(lineUuid, e.clientX, e.clientY);
                      })
                    }
                    selectedRouteUuids={selectedRouteUuids}
                    filteredRouteUuids={filteredRouteUuids}
                  />
                )}
              </g>
            </g>
          );
        }}
      />
      {routesPending ? <MapProgress /> : null}
    </Box>
  );
};

export const SportMaps = ({
  routes,
  lines,
  canChangeSelection,
  onSelectSingleRoute,
  onClearSelection,
  selectedRouteUuids,
  onSelectLine,
  onSelectRoutes,
  onDeselectRoutes,
  onSetSelectedRouteUuids,
  onRouteContextMenu,
  onLineContextMenu,
  maps,
  filteredRouteUuids,
  brushEnabled,
  routesPending,
  mapsPending,
}: // onZoomChange,
SportProps) => {
  const { mapPositionEdits, routeFormEdits } = useRoutesUiStore(
    useShallow((state) => ({
      mapPositionEdits: state.mapPositionEdits,
      routeFormEdits: state.routeFormEdits,
    })),
  );

  const isShiftPressedRef = useIsKeyDownRef("Shift");

  const [expandedLineUuid, setExpandedLineUuid] = useState<string | null>(null);
  const routesRef = useRef(routes);
  const expandedLineUuidRef = useRef(expandedLineUuid);
  useEffect(() => {
    routesRef.current = routes;
  }, [routes]);

  useEffect(() => {
    if (selectedRouteUuids.size === 1) {
      const route = routesRef.current?.find((r) => selectedRouteUuids.has(r.uuid));
      const lineUuid =
        route &&
        (route.startLine?.uuid ||
          routeFormEdits[route.uuid]?.startLine?.uuid ||
          mapPositionEdits[route.uuid]?.line);
      if (lineUuid) {
        setExpandedLineUuid(lineUuid);
      } else {
        setExpandedLineUuid(null);
      }
    } else if (expandedLineUuidRef.current && selectedRouteUuids.size === 0) {
      setExpandedLineUuid(null);
    }
  }, [selectedRouteUuids, mapPositionEdits, routeFormEdits]);

  const onClick = useCallback(
    (uuid: string) => {
      if (!canChangeSelection) {
        return;
      }
      const isSelected = selectedRouteUuids.has(uuid);
      console.log({
        isSelected,
        isShiftPressed: isShiftPressedRef.current,
        uuid,
      });
      if (isShiftPressedRef.current) {
        if (isSelected) {
          onDeselectRoutes([uuid]);
        } else {
          onSelectRoutes([uuid]);
        }
      } else {
        if (isSelected) {
          onClearSelection();
        } else {
          onSelectSingleRoute(uuid);
        }
      }
    },
    [
      canChangeSelection,
      selectedRouteUuids,
      onSelectSingleRoute,
      onSelectRoutes,
      onDeselectRoutes,
      onClearSelection,
      isShiftPressedRef,
    ],
  );

  const linesRoutesByMap = useMemo(() => {
    if (!routes || !lines) {
      return emptyObject as { ""?: Partial<Route>[] } & Record<
        string,
        { lineRoutes: LineRoutes[]; nonLineRoutes: Partial<Route>[] }
      >;
    }

    const routesWithEdits = routes.map((route) => {
      const editedPos = mapPositionEdits[route?.uuid];

      const formEditedLineUuid = routeFormEdits[route?.uuid]?.startLine?.uuid;
      const editedLineUuid = editedPos?.line;

      if (editedPos?.map && editedPos.x !== null && editedPos.y !== null) {
        return {
          ...route,
          map: editedPos.map,
          mapPosition: { x: editedPos.x, y: editedPos.y },
          startLine: formEditedLineUuid
            ? {
                uuid: formEditedLineUuid,
                name: "",
                isActive: true,
                canLead: true,
                canToprope: true,
              }
            : editedLineUuid
              ? {
                  uuid: editedLineUuid,
                  name: "",
                  isActive: true,
                  canLead: true,
                  canToprope: true,
                }
              : route.startLine,
        };
      }
      return route;
    });

    const result: { ""?: Partial<Route>[] } & Record<
      string,
      { lineRoutes: LineRoutes[]; nonLineRoutes: Partial<Route>[] }
    > = {};
    const routesByLine = groupBy(routesWithEdits, (route) => {
      return route.startLine?.uuid || "";
    });
    result[""] = routesByLine[""];

    const routesByMap = groupBy(routesWithEdits, (route) => {
      return route.map || "";
    });

    // Initialize map data
    maps.forEach((map) => {
      result[map.uuid] = { lineRoutes: [], nonLineRoutes: [] };
    });

    lines.forEach((line) => {
      const lineRoutes = routesByLine[line.uuid] || [];
      if (line.mapPosition && result[line.map]) {
        result[line.map].lineRoutes.push({
          line,
          routes: lineRoutes,
          mapPosition: line.mapPosition,
        });
      }
    });

    // Add non-line routes for each map
    maps.forEach((map) => {
      const mapRoutes = routesByMap[map.uuid] || [];
      const nonLineRoutes = mapRoutes.filter(
        (route) =>
          !route.startLine &&
          !mapPositionEdits[route?.uuid]?.line &&
          !routeFormEdits[route?.uuid]?.startLine?.uuid &&
          route.mapPosition,
      );

      if (result[map.uuid]) {
        result[map.uuid].nonLineRoutes = nonLineRoutes;
      }
    });

    return result;
  }, [routes, lines, mapPositionEdits, routeFormEdits, maps]);

  const onSelectLines = useCallback(
    (_lineUuids: string[], routeUuids: string[]) => {
      if (!canChangeSelection) {
        return;
      }
      if (isShiftPressedRef.current) {
        onSelectRoutes(routeUuids);
      } else {
        onSetSelectedRouteUuids(routeUuids);
      }
    },
    [canChangeSelection, onSelectRoutes, onSetSelectedRouteUuids, isShiftPressedRef],
  );

  const selectedLineUuids = useMemo(() => {
    return new Set(
      routes
        ?.filter(
          (route) =>
            selectedRouteUuids.has(route.uuid) &&
            (route.startLine || mapPositionEdits[route.uuid]?.line),
        )
        .map((route) => (route.startLine?.uuid || mapPositionEdits[route.uuid]?.line)!),
    );
  }, [selectedRouteUuids, routes, mapPositionEdits]);

  const showUnplacedRoutes = useMemo(() => {
    return !!routes?.find((route) => !route.map && !route.startLine);
  }, [routes]);

  return (
    <Stack
      direction="row"
      sx={{
        minHeight: 200,
        backgroundColor: "var(--joy-palette-neutral-900)",
        height: "100%",
        position: "relative",
      }}
    >
      {showUnplacedRoutes && (
        <UnplacedRoutesDrawer
          routes={(linesRoutesByMap[""] || emptyArray).filter(
            (route): route is Partial<Route> & { uuid: string } =>
              !!route.uuid && (!route.map || !route.mapPosition),
          )}
          onClick={onClick}
          onContextMenu={onRouteContextMenu}
        />
      )}
      <MapConfigDropdown />
      {mapsPending ? <MapProgress /> : null}
      {maps.map((map) => {
        const mapData = linesRoutesByMap[map.uuid];
        return (
          <SportMap
            map={map}
            key={map.uuid}
            linesRoutes={mapData?.lineRoutes || emptyArray}
            nonLineRoutes={mapData?.nonLineRoutes || emptyArray}
            onClick={onClick}
            onSelectLine={onSelectLine}
            onSelectLines={onSelectLines}
            selectedLineUuids={selectedLineUuids}
            expandedLineUuid={expandedLineUuid}
            onExpandLine={setExpandedLineUuid}
            selectedRouteUuids={selectedRouteUuids}
            onRouteContextMenu={onRouteContextMenu}
            onLineContextMenu={onLineContextMenu}
            canChangeSelection={canChangeSelection}
            brushEnabled={brushEnabled}
            routesPending={routesPending}
            filteredRouteUuids={filteredRouteUuids}
          />
        );
      })}
      <CurrentlyDraggingRoutePin />
    </Stack>
  );
};

function getScaleFactor(isSelected: boolean, isFilteredOut: boolean, hasSelection: boolean) {
  if (isSelected) {
    if (isFilteredOut) {
      return 0.5;
    } else {
      return 1.25;
    }
  }
  if (isFilteredOut) {
    return 0.25;
  }
  return hasSelection ? 0.75 : 1;
}
